import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {
    deleteQuoteTemplates,
    getQuoteTemplates,
    getQuoteTemplatesStatus,
    selectGetQuoteTemplatesById
} from "../../redux/ProjectsSlice";
import {useTheme} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {useSnackbar} from "notistack";
import {getFields, getFieldsArray, getFieldsStatus} from "../../redux/ProductsSlice";
import {GridActionsCellItem} from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TemplatesDataGrid from "../../Components/Templates/TemplatesDataGrid";
import Moment from "moment";
import EditTemplateDialog from "../../Components/Templates/EditTemplateDialog";
import TrimObjectStrings from "../../Helpers/TrimObjectStrings";
import LoadingOverlay from "../../Components/Loading/LoadingOverlay";
import DeleteConfirmationDialog from "../../Components/Dialogs/DeleteConfirmationDialog";

const ManageTemplates = () => {
    useEffect(() => {
        document.title = "Manage Templates - CPC";
    }, []);

    const dispatch = useDispatch();
    const templates = useSelector(selectGetQuoteTemplatesById);
    const templatesStatus = useSelector(getQuoteTemplatesStatus);

    const { enqueueSnackbar } = useSnackbar();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const existingFields = useSelector(getFieldsArray);
    const existingFieldsStatus = useSelector(getFieldsStatus);
    
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down("sm"));

    const [rows, setRows] = useState([]);
    const [chosenRows, setChosenRows] = useState(null);
    const [rowToEdit, setRowToEdit] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [rowModesModel, setRowModesModel] = useState({});
    
    const handleEditClick = (id) => () => {
        setRowToEdit(rows.find((row) => row.id === id));
        setOpenDialog(true);
    }
    
    const handleDeleteClick = (id) => {
        setIsSubmitting(true);
        let chosenIds = [id];
        if (!id) {
            chosenIds = chosenRows;
        }
        
        dispatch(
            deleteQuoteTemplates(
                TrimObjectStrings({
                    payload: {
                        ids: [...chosenIds],
                    },
                })
            )
        )
            .unwrap()
            .then(() => {
                setIsSubmitting(false);
                
                enqueueSnackbar("Template successfully deleted!", {
                    variant: "success",
                    autoHideDuration: 3000, 
                });
            })
            .catch((error) => {
                let errorMessage = "Failed to delete template. Please try again.";
            
                console.error("Failed to delete template", error);
                setIsSubmitting(false);
                enqueueSnackbar(errorMessage, {
                    variant: "error",
                    autoHideDuration: 3000,
                })
            })
    }

    useEffect(() => {
        if (templatesStatus === "idle") {
            dispatch(getQuoteTemplates());
        }
        if (existingFieldsStatus === "idle") {
            dispatch(getFields());
        }
    }, [dispatch, templatesStatus, existingFieldsStatus]);

    useEffect(() => {
        if (templates && templates.length > 0) {
            setRows(templates);
        }
    }, [templates]);

    const columns = [
        {
            field: "name",
            headerName: "Name",
            width: isXs ? 160 : 230,
            headerClassName: "new-param-theme--header",
        },
        {
            field: "product",
            headerName: "Product",
            width: isXs ? 180 : 250,
            headerClassName: "new-param-theme--header",
            valueGetter: (params, row) => {
                return row.productGroup?.name ?? row.productItem?.name;
            },
        },
        {
            field: "lastUsed",
            headerName: "Last Used",
            width: isXs ? 120 : 180,
            headerClassName: "new-param-theme--header",
            valueGetter: (value) => {
                return Moment(value).format('D MMM YYYY, h:mma')
            }
        },
        {
            field: "createdAt",
            headerName: "Created At",
            width: isXs ? 120 : 180,
            headerClassName: "new-param-theme--header",
            valueGetter: (value) => {
                return Moment(value).format('D MMM YYYY, h:mma')
            }
        },
        {
            field: "createdBy",
            headerName: "Created By",
            width: isXs ? 120 : 150,
            headerClassName: "new-param-theme--header",
        },
        {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            headerClassName: "new-param-theme--header",
            width: 100,
            cellClassName: "actions",
            getActions: (params) => {
                return [
                    <GridActionsCellItem
                        icon={<EditIcon color='secondary' />}
                        label='Edit'
                        className='textPrimary'
                        onClick={handleEditClick(params.id)}
                        color='inherit'
                    />,
                    <DeleteConfirmationDialog
                        header="Delete Confirmation"
                        title="Are you sure you want to delete this template?"
                        description="This will remove it as an option when creating new quotes."
                        response={() => handleDeleteClick(params.id)}
                    >
                        {(showDialog) => (
                            <GridActionsCellItem
                                icon={<DeleteIcon color='error' />}
                                label='Delete'
                                onClick={showDialog}
                                color='inherit'
                            />
                        )}
                    </DeleteConfirmationDialog>
                ];
            },
        },
    ];

    const handleRowModesModelChange = (newRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };
    
    return <div>
        {isSubmitting && <LoadingOverlay />}
        {openDialog &&
            <EditTemplateDialog
                openDialog={openDialog}
                handleClose={() => {
                    setOpenDialog(false);
                    setRowToEdit(null);
                }}
                templateToEdit={rowToEdit}
                existingFields={existingFields}
                dispatcher={dispatch}
            />
        }
        <Grid
            container
            spacing={3}
            justifyContent='space-between'
        >
            <Grid size={{ xs: 12}}>
                <Typography variant='h2'>Manage Templates</Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
                <TemplatesDataGrid
                    allRows={rows}
                    columns={columns}
                    rowModesModel={rowModesModel}
                    setRowModesModel={setRowModesModel}
                    chosenRows={chosenRows}
                    setChosenRows={setChosenRows}
                    handleRowModesModelChange={handleRowModesModelChange}
                    loading={templatesStatus === "loading"}
                    handleDeleteItems={handleDeleteClick}
                />
            </Grid>
        </Grid>
    </div>
}

export default ManageTemplates;